import { fetchJSON as fetchAPI } from './client';

export type ExperimentDashboard = {
  tenant_id: string;
  active_experiments: number;
  completed_experiments: number;
  pending_approval: number;
  winning_variants: number;
  avg_lift_pct: number;
  avg_confidence: number;
  learning_updates: number;
  mye_connected: boolean;
  active_recommendations: number;
  generated_at?: string;
};

export type ExperimentRecord = {
  id: string;
  name: string;
  type: string;
  sku?: string;
  asin?: string;
  status: string;
  source_module?: string;
  primary_metric?: string;
  hypothesis?: string;
  expected_lift_pct: number;
  confidence_level: number;
  required_sample_size: number;
  expected_duration?: string;
  risk_level?: string;
  mye_eligible: boolean;
  mye_ref?: string;
  winner_variant?: string;
  requires_approval: boolean;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
};

export type VariantRecord = {
  id: string;
  experiment_id: string;
  label: string;
  description?: string;
  content?: string;
  is_baseline: boolean;
  created_at?: string;
};

export type ExperimentResult = {
  id: string;
  experiment_id: string;
  variant_id: string;
  variant_label: string;
  impressions: number;
  clicks: number;
  orders: number;
  ctr: number;
  conversion_rate: number;
  revenue: number;
  profit: number;
  return_rate_pct: number;
  is_winner: boolean;
  generated_at?: string;
};

export type StatisticalAnalysis = {
  id: string;
  experiment_id: string;
  confidence_level: number;
  statistical_significance: number;
  observed_lift_pct: number;
  confidence_interval?: string;
  min_sample_size: number;
  current_sample: number;
  is_significant: boolean;
  is_directional_only: boolean;
  outliers_detected: boolean;
  incomplete: boolean;
  winner_variant?: string;
  generated_at?: string;
};

export type ExperimentRecommendation = {
  id: string;
  title: string;
  description?: string;
  experiment_type?: string;
  sku?: string;
  expected_impact?: string;
  expected_lift_pct: number;
  confidence_score: number;
  required_sample_size: number;
  expected_duration?: string;
  risk_level?: string;
  expected_roi: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
  created_at?: string;
};

export type LearningRecord = {
  id: string;
  experiment_id: string;
  dna_target: string;
  learning: string;
  impact?: string;
  version: number;
  created_at?: string;
};

export type CreateExperimentRequest = {
  name: string;
  type: string;
  sku?: string;
  hypothesis?: string;
  primary_metric?: string;
  expected_lift_pct?: number;
};

const API_BASE = import.meta.env.VITE_EXPINTEL_API ?? '/api/v1/expintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const expintelApi = {
  dashboard: () => fetchJSON<ExperimentDashboard>('/dashboard'),
  listExperiments: () => fetchJSON<ExperimentRecord[]>('/experiments'),
  createExperiment: (body: CreateExperimentRequest) =>
    fetchJSON<ExperimentRecord>('/experiments', { method: 'POST', body: JSON.stringify(body) }),
  approveExperiment: (id: string) =>
    fetchJSON<ExperimentRecord>(`/experiments/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listVariants: (experimentId?: string) =>
    fetchJSON<VariantRecord[]>(experimentId ? `/variants?experiment_id=${experimentId}` : '/variants'),
  listResults: () => fetchJSON<ExperimentResult[]>('/results'),
  listStatistics: () => fetchJSON<StatisticalAnalysis[]>('/statistics'),
  listRecommendations: () => fetchJSON<ExperimentRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<ExperimentRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listLearning: () => fetchJSON<LearningRecord[]>('/learning'),
  listActivity: () => fetchJSON<{ id: string; message: string; category?: string }[]>('/activity'),
};
